/**
 * Exames toxicológicos do motorista profissional (Lei 13.103/2015).
 *
 * A listagem existe para responder duas perguntas que ninguém consegue
 * responder olhando o ASO: quais laudos ainda não voltaram do laboratório, e
 * quais motoristas estão perto de vencer os 30 meses.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Chip, Alert, Button } from '@mui/material';
import {
  Science as ScienceIcon,
  Send as SendIcon,
  Add as AddIcon,
  Biotech as BiotechIcon,
} from '@mui/icons-material';
import { PageShell, DataTable, type DataTableColumn } from '../../components';
import { RegistrarColetaDialog } from './components/RegistrarColetaDialog';
import { LancarLaudoDialog } from './components/LancarLaudoDialog';
import toxicologicalExamService from '../../services/toxicologicalExamService';
import type { ToxicologicalExam } from '../../types/toxicologicalExam';
import { RESULTADO_LABELS } from '../../types/toxicologicalExam';

/** Formata ISO (yyyy-mm-dd) em pt-BR sem passar por Date — evita o erro de fuso. */
function dataBR(iso: string | null): string {
  if (!iso) return '—';
  const [ano, mes, dia] = iso.slice(0, 10).split('-');
  return `${dia}/${mes}/${ano}`;
}

function diasAte(iso: string | null): number | null {
  if (!iso) return null;
  const alvo = new Date(`${iso.slice(0, 10)}T00:00:00`).getTime();
  const hoje = new Date(new Date().toISOString().slice(0, 10) + 'T00:00:00').getTime();
  return Math.round((alvo - hoje) / 86_400_000);
}

function corDoResultado(result: string | null): 'success' | 'error' | 'warning' | 'default' {
  if (result === 'NEGATIVO') return 'success';
  if (result === 'POSITIVO') return 'error';
  if (result === 'INCONCLUSIVO') return 'warning';
  return 'default';
}

const colunas: DataTableColumn<ToxicologicalExam>[] = [
  {
    id: 'collectionDate',
    label: 'Coleta',
    primary: true,
    render: (e) => dataBR(e.collectionDate),
  },
  {
    id: 'result',
    label: 'Resultado',
    render: (e) =>
      e.result ? (
        <Chip
          size="small"
          label={RESULTADO_LABELS[e.result] ?? e.result}
          color={corDoResultado(e.result)}
        />
      ) : (
        <Chip size="small" label="Aguardando laudo" color="default" variant="outlined" />
      ),
  },
  {
    id: 'laboratoryCnpj',
    label: 'Laboratório',
    render: (e) => e.laboratoryCnpj ?? '—',
  },
  {
    id: 'examSequenceCode',
    label: 'Código do exame',
    render: (e) => e.examSequenceCode ?? '—',
  },
  {
    id: 'proximoVencimento',
    label: 'Próximo em',
    render: (e) => {
      const dias = diasAte(e.proximoVencimento);
      if (dias === null) return '—';
      if (dias < 0) return <Chip size="small" label="Vencido" color="error" />;
      if (dias <= 60) return <Chip size="small" label={`${dias} dias`} color="warning" />;
      return dataBR(e.proximoVencimento);
    },
  },
  {
    id: 'prontoParaEsocial',
    label: 'eSocial',
    render: (e) =>
      e.prontoParaEsocial ? (
        <Chip size="small" icon={<SendIcon />} label="Pronto" color="success" variant="outlined" />
      ) : (
        <Chip size="small" label="Incompleto" color="default" variant="outlined" />
      ),
  },
];

export const ToxicologicalExamsListPage: React.FC = () => {
  const [exames, setExames] = useState<ToxicologicalExam[]>([]);
  const [coletaAberta, setColetaAberta] = useState(false);
  const [exameDoLaudo, setExameDoLaudo] = useState<ToxicologicalExam | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      setExames(await toxicologicalExamService.listar());
    } catch (err: unknown) {
      setErro(err instanceof Error ? err.message : 'Não foi possível carregar os exames');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const aguardandoLaudo = useMemo(() => exames.filter((e) => !e.result).length, [exames]);

  return (
    <PageShell
      breadcrumbs={[{ label: 'Início', path: '/' }, { label: 'Exame Toxicológico' }]}
      title="Exame Toxicológico"
      subtitle="Motoristas profissionais — coleta, laudo e envio do S-2221 (Lei 13.103/2015)"
      actions={
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setColetaAberta(true)}>
          Registrar coleta
        </Button>
      }
    >
      {erro && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {erro}
        </Alert>
      )}

      {aguardandoLaudo > 0 && (
        <Alert severity="info" icon={<ScienceIcon />} sx={{ mb: 2 }}>
          {aguardandoLaudo === 1
            ? '1 coleta aguardando o laudo do laboratório.'
            : `${aguardandoLaudo} coletas aguardando o laudo do laboratório.`}{' '}
          Sem o laudo, o S-2221 não pode ser transmitido.
        </Alert>
      )}

      <DataTable
        columns={colunas}
        rows={exames}
        getRowId={(e) => e.id}
        loading={carregando}
        emptyState={{
          title: 'Nenhuma coleta de toxicológico registrada nesta empresa',
          description:
            'O exame toxicológico é obrigatório para motorista profissional na admissão, no desligamento e a cada 30 meses. A coleta é registrada a partir do atendimento do trabalhador.',
        }}
        actions={[
          {
            icon: <BiotechIcon fontSize="small" />,
            tooltip: 'Lançar laudo do laboratório',
            onClick: (e: ToxicologicalExam) => setExameDoLaudo(e),
          },
        ]}
      />

      <RegistrarColetaDialog
        aberto={coletaAberta}
        onFechar={() => setColetaAberta(false)}
        onSalvo={() => void carregar()}
      />

      <LancarLaudoDialog
        exame={exameDoLaudo}
        aberto={exameDoLaudo !== null}
        onFechar={() => setExameDoLaudo(null)}
        onSalvo={() => void carregar()}
      />
    </PageShell>
  );
};

export default ToxicologicalExamsListPage;
